/**
 * Agent Push Key card (R20 chat 4)
 *
 * Core-owned, rendered by pages/ModuleSettings.tsx for any module that
 * declares agent properties — whether or not the module ships a settings page
 * of its own. Mint, regenerate and revoke the key an external agent uses to
 * push into this module's properties on this channel.
 *
 * THE KEY IS SHOWN ONCE, in a copy field inside this card, and lives only in
 * component state until the card is dismissed or the page is left. It is never
 * put in a toast and never logged. Outcomes go to useToast; failures read
 * apiErrorMessage.
 */

import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Check, Copy, KeyRound, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { apiErrorMessage } from '@/api/client'
import {
  type AgentKeyMinted,
  type AgentKeyStatus,
  getAgentKey,
  mintAgentKey,
  revokeAgentKey,
} from '@/api/agentKeysApi'
import { useToast } from '@/components/Toast'

function when(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleString()
}

/** Read-only value with a copy button. */
function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Insecure context: the value is still selectable in the input.
    }
  }
  return (
    <div>
      <label className="block text-sm text-text-secondary mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="input font-mono text-sm flex-1"
          autoComplete="off"
          spellCheck={false}
        />
        <button type="button" onClick={copy} className="btn-secondary flex items-center gap-2 shrink-0">
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

export default function AgentPushKeyCard({
  moduleName,
  channelId,
}: {
  moduleName: string
  channelId: number
}) {
  const toast = useToast()
  const [status, setStatus] = useState<AgentKeyStatus | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [minted, setMinted] = useState<AgentKeyMinted | null>(null)

  const load = useCallback(async () => {
    try {
      setStatus(await getAgentKey(moduleName, channelId))
      setLoadError(null)
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Failed to load the push key status'))
    }
  }, [moduleName, channelId])

  useEffect(() => {
    // A different channel is a different key: never carry a shown key across.
    setMinted(null)
    setStatus(null)
    load()
  }, [load])

  const mint = async () => {
    if (
      status?.exists &&
      !confirm('Regenerate the push key? The current key stops working immediately.')
    ) {
      return
    }
    setBusy(true)
    try {
      const result = await mintAgentKey(moduleName, channelId)
      setMinted(result)
      toast.success(result.regenerated ? 'Push key regenerated' : 'Push key created')
      await load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to create the push key'))
    } finally {
      setBusy(false)
    }
  }

  const revoke = async () => {
    if (!confirm('Revoke the push key? The agent will be refused until a new key is created.')) {
      return
    }
    setBusy(true)
    try {
      await revokeAgentKey(moduleName, channelId)
      setMinted(null)
      toast.success('Push key revoked')
      await load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to revoke the push key'))
    } finally {
      setBusy(false)
    }
  }

  const pushUrl = (path: string) => `${window.location.origin}${path}`

  return (
    <div className="card space-y-4">
      <div className="flex items-start gap-3">
        <KeyRound className="text-synth-cyan shrink-0 mt-0.5" size={20} />
        <div>
          <h3 className="text-lg font-medium text-text-primary">Agent push key</h3>
          <p className="text-sm text-text-secondary">
            Lets a program on another machine push this module's data for this channel. The key can
            only push — it cannot sign in or change anything else.
          </p>
        </div>
      </div>

      {loadError && (
        <div className="p-3 bg-state-danger-bg border border-state-danger rounded-lg flex items-start gap-2 text-state-danger text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!status && !loadError && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      )}

      {status && (
        <>
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-text-secondary">Status</dt>
            <dd className="text-text-primary">{status.exists ? 'Key active' : 'No key'}</dd>
            <dt className="text-text-secondary">Accepts</dt>
            <dd className="text-text-primary font-mono">{status.properties.join(', ')}</dd>
            {status.exists && (
              <>
                <dt className="text-text-secondary">Issued</dt>
                <dd className="text-text-primary">{when(status.regenerated_at ?? status.created_at)}</dd>
                <dt className="text-text-secondary">Last push</dt>
                <dd className="text-text-primary">{when(status.last_used_at)}</dd>
              </>
            )}
          </dl>

          {minted && (
            <div className="space-y-4">
              <div className="p-3 bg-state-warning-bg border border-state-warning rounded-lg flex items-start gap-2 text-state-warning text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>
                  This is the only time the key is shown. Put it in the agent's configuration now. Send
                  it as an <span className="font-mono">Authorization: Bearer</span> header — never in
                  the URL. If you lose it, regenerate.
                </span>
              </div>
              <CopyValue label="Push URL (POST, JSON)" value={pushUrl(minted.push_path)} />
              <CopyValue label="Push key" value={minted.key} />
              <div className="flex justify-end">
                <button type="button" className="btn-secondary" onClick={() => setMinted(null)}>
                  Done — hide the key
                </button>
              </div>
            </div>
          )}

          {!minted && status.exists && (
            <CopyValue label="Push URL (POST, JSON)" value={pushUrl(status.push_path)} />
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              onClick={mint}
              disabled={busy}
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : status.exists ? <RefreshCw size={16} /> : <KeyRound size={16} />}
              {status.exists ? 'Regenerate key' : 'Create key'}
            </button>
            {status.exists && (
              <button
                type="button"
                className="btn-secondary flex items-center gap-2"
                onClick={revoke}
                disabled={busy}
              >
                <Trash2 size={16} />
                Revoke
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
